// utils/image.ts
export const ACCEPTED_MIME = ["image/jpeg", "image/png"];
const MAX_SIZE_BYTES = 3 * 1024 * 1024;
const MAX_DIMENSION = 2048;

export async function validateImage(file: File) {
  if (!file) throw new Error("No file provided");
  if (!ACCEPTED_MIME.includes(file.type)) {
    throw new Error("Unsupported image type");
  }
  if (file.size > MAX_SIZE_BYTES) {
    throw new Error("File size exceeds 3MB");
  }
  const dims = await getImageDimensions(file);
  if (dims.width > 10000 || dims.height > 10000) {
    throw new Error("Image dimensions are too large");
  }
  return true;
}

export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((res, rej) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      URL.revokeObjectURL(url);
      res({ width, height });
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      rej(new Error("Unable to read image"));
    };
    img.src = url;
  });
}

export async function resizeImage(file: File, maxWidth = MAX_DIMENSION, quality = 0.8, maxHeight?: number): Promise<Blob> {
  const dims = await getImageDimensions(file);

  let scale = 1;
  if (maxHeight) {
    scale = Math.min(1, maxWidth / dims.width, maxHeight / dims.height);
  } else {
    scale = Math.min(1, maxWidth / Math.max(dims.width, dims.height));
  }

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(dims.width * scale);
  canvas.height = Math.round(dims.height * scale);
  const ctx = canvas.getContext("2d")!;
  const img = new Image();
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (!blob) return reject(new Error("Compression failed"));
        resolve(blob);
      }, "image/jpeg", quality);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(new Error("Resizing failed"));
    };
    img.src = url;
  });
}
